import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { NotificationService } from '../../../core/services/notification.service';

const ICON: Record<string, string> = {
  success: '✓',
  error: '✕',
  warning: '!',
  info: 'ℹ',
};

@Component({
  selector: 'app-toast',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="toast-region" role="region" aria-label="Notifications">
      @for (toast of notify.toasts(); track toast.id) {
        <div class="toast" [class]="'toast--' + toast.type" role="status">
          <span class="toast__icon" aria-hidden="true">{{ icon(toast.type) }}</span>
          <span class="toast__message">{{ toast.message }}</span>
          <button
            type="button"
            class="toast__dismiss"
            (click)="notify.dismiss(toast.id)"
            aria-label="Dismiss notification"
          >
            ✕
          </button>
        </div>
      }
    </div>
  `,
  styles: `
    .toast-region {
      position: fixed;
      top: var(--space-5);
      right: var(--space-5);
      display: flex;
      flex-direction: column;
      gap: var(--space-3);
      z-index: 300;
      max-width: min(360px, calc(100vw - 2 * var(--space-5)));
    }

    .toast {
      display: flex;
      align-items: flex-start;
      gap: var(--space-3);
      padding: var(--space-4);
      border-radius: var(--radius-md);
      box-shadow: var(--shadow-lg);
      background: var(--color-surface);
      border: 1px solid var(--color-border);
      animation: toast-in var(--transition-base);
    }

    .toast__icon {
      font-weight: 700;
      flex-shrink: 0;
    }

    .toast__message {
      flex: 1;
      font-size: var(--fs-sm);
      color: var(--color-neutral-800);
    }

    .toast__dismiss {
      background: none;
      border: none;
      cursor: pointer;
      color: var(--color-text-muted);
      font-size: var(--fs-xs);
      line-height: 1;
      padding: var(--space-1);
    }

    .toast--success {
      border-left: 4px solid var(--color-success-500);
    }
    .toast--success .toast__icon {
      color: var(--color-success-600);
    }

    .toast--error {
      border-left: 4px solid var(--color-danger-500);
    }
    .toast--error .toast__icon {
      color: var(--color-danger-600);
    }

    .toast--warning {
      border-left: 4px solid var(--color-warning-500);
    }
    .toast--warning .toast__icon {
      color: var(--color-warning-600);
    }

    .toast--info {
      border-left: 4px solid var(--color-info-500);
    }
    .toast--info .toast__icon {
      color: var(--color-info-600);
    }

    @keyframes toast-in {
      from {
        opacity: 0;
        transform: translateY(-8px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    @media (max-width: 600px) {
      .toast-region {
        left: var(--space-4);
        right: var(--space-4);
        max-width: none;
      }
    }
  `,
})
export class ToastComponent {
  protected readonly notify = inject(NotificationService);

  protected icon(type: string): string {
    return ICON[type] ?? 'ℹ';
  }
}
